import React, { useCallback, useMemo, useState } from "react";
import Select from "react-select";
import TagFilterBar from "../Components/TagFilterBar";
import { questionMatchesSteps } from "../Components/TagFilterUtils";
import { getLatestFY } from "../../DataManagment/Quantative/component/answerHistoryPage/components/utils";
import DueDateRow from "./DueDateRow";
import DueDateEditorModal from "./DueDateEditorModal";
import BulkOverwriteModal from "./BulkOverwriteModal";
import { usePermission } from "../../../hooks/usePermission";
import {
  indexDueDates,
  locationLabel,
  resolveRowDueDate,
  todayISO,
  useDueDates,
} from "./useDueDates";
import { clearDueDate, resyncDatasets, setDueDatesBulk } from "./dueDatesApi";
import "./DueDates.css";

/** Scope stored when a date is set against one KPI. */
const KPI_SCOPE = "KPI";

/** Questionnaire this screen configures. */
const QUANT = "QUANT";

/** react-select styling aligned to the toolbar's native controls. */
const LOCATION_SELECT_STYLES = {
  control: (base) => ({
    ...base,
    minHeight: 38,
    borderWidth: 1.5,
    borderColor: "#cbd5e1",
    borderRadius: 8,
    fontSize: 13,
    boxShadow: "none",
    ":hover": { borderColor: "#cbd5e1" },
  }),
  valueContainer: (base) => ({ ...base, padding: "1px 8px" }),
  menu: (base) => ({ ...base, zIndex: 20, fontSize: 13 }),
  placeholder: (base) => ({ ...base, color: "#64748b" }),
};

/**
 * Whether a KPI passes the current list filters (locations, tag steps, search).
 * Shared by the visible list and the bulk-selection prune so both agree on
 * what is on screen.
 *
 * With no location chosen every KPI is in scope; with several chosen a KPI is in
 * scope when it is assigned to any one of them (the union), so widening the
 * selection only ever reveals more rows.
 * @param {object} kpi the KPI row.
 * @param {Map<number, Set<number>>} assignments KPI id → assigned location ids.
 * @param {{locationIds: number[], filterSteps: object[], search: string}} filters
 *   the active filters.
 * @returns {boolean} true when the KPI is visible under the filters.
 */
function passesFilters(kpi, assignments, { locationIds, filterSteps, search }) {
  const term = (search || "").trim().toLowerCase();
  if (locationIds.length) {
    const assigned = assignments.get(Number(kpi.kpiId));
    if (!assigned || !locationIds.some((id) => assigned.has(id))) return false;
  }
  if (!questionMatchesSteps(kpi, filterSteps)) return false;
  if (term && !kpi.title?.toLowerCase().includes(term)) return false;
  return true;
}

/**
 * Keeps the leading run of tag-filter steps still satisfiable within a scoped
 * KPI set. The Module filter's options follow the chosen locations, so when the
 * location selection narrows, a previously-picked module that no longer exists
 * there must drop too — otherwise a dangling chip leaves the list empty with no
 * way to clear it. Steps drill down, so the first unsatisfiable step and every
 * step after it are removed together.
 * @param {object[]} steps the active tag-filter steps.
 * @param {object[]} scopedKpis the KPIs in scope after the location change.
 * @returns {object[]} the still-valid leading steps.
 */
function pruneStepsToScope(steps, scopedKpis) {
  const kept = [];
  for (const step of steps) {
    const candidate = [...kept, step];
    if (scopedKpis.some((kpi) => questionMatchesSteps(kpi, candidate))) {
      kept.push(step);
    } else {
      break;
    }
  }
  return kept;
}

/**
 * Set Due Date — the deadlines that lock KPI data entry once passed.
 *
 * Dates are set at three breadths: a whole location, a whole module, or an
 * individual KPI. The narrowest one wins, which is why each row shows where its
 * date came from — so it's obvious when a KPI is following a broader rule
 * rather than one of its own.
 * @returns {JSX.Element}
 */
export default function DueDates() {
  const fyData = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("financialYears")) ?? [];
    } catch {
      return [];
    }
  }, []);
  const latestFY = useMemo(() => getLatestFY(fyData), [fyData]);
  const [selectedFY, setSelectedFY] = useState(() => latestFY);
  const [canManageDueDates] = usePermission("due-dates.manage", "DUE_DATES");
  const isViewOnly = selectedFY?.id !== latestFY?.id || !canManageDueDates;

  const { kpis, assignments, locations, dueDates, loading, error, refresh } =
    useDueDates(selectedFY);

  const [locationIds, setLocationIds] = useState(() => []);
  const [search, setSearch] = useState("");
  const [filterSteps, setFilterSteps] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [pending, setPending] = useState(() => new Map());
  const [bulkDate, setBulkDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [editingKpi, setEditingKpi] = useState(null);
  const [confirmOverwrite, setConfirmOverwrite] = useState(false);

  const index = useMemo(() => indexDueDates(dueDates), [dueDates]);

  /**
   * The in-view locations a KPI actually belongs to — the intersection of the
   * chosen locations and the KPI's assignments — so it only inherits a location
   * deadline from a location it is genuinely part of.
   * @param {object} kpi the KPI row.
   * @returns {number[]} the applicable location ids.
   */
  const applicableLocations = useCallback(
    (kpi) => {
      if (!locationIds.length) return [];
      const assigned = assignments.get(Number(kpi.kpiId));
      return assigned ? locationIds.filter((id) => assigned.has(id)) : [];
    },
    [assignments, locationIds],
  );

  /**
   * Whether a KPI already has an effective saved deadline — its own, or one it
   * inherits from its location or module. Bulk apply leaves these untouched: a
   * date already in force is only changed deliberately, one KPI at a time via
   * Configure, never swept over by a bulk fill.
   * @param {object} kpi the KPI row.
   * @returns {boolean} true when a saved deadline is already in force.
   */
  const kpiHasDate = useCallback(
    (kpi) => resolveRowDueDate(kpi, applicableLocations(kpi), index) != null,
    [applicableLocations, index],
  );

  /** The deadline in force for the KPI being configured, if any. */
  const editingResolved = useMemo(
    () =>
      editingKpi
        ? resolveRowDueDate(editingKpi, applicableLocations(editingKpi), index)
        : null,
    [editingKpi, applicableLocations, index],
  );

  /** The id of the KPI's own saved date (null when it only inherits one). */
  const editingOwnId = editingKpi
    ? index.byKpi.get(Number(editingKpi.kpiId))?.id ?? null
    : null;

  /**
   * The KPIs a deadline can be set on right now.
   *
   * Choosing a location narrows the list to the KPIs assigned there, because a
   * location deadline covers that location's assigned KPIs — showing the rest
   * would offer a date that never reaches them.
   */
  const visibleKpis = useMemo(
    () =>
      kpis.filter((kpi) =>
        passesFilters(kpi, assignments, { locationIds, filterSteps, search }),
      ),
    [kpis, assignments, locationIds, filterSteps, search],
  );

  /**
   * The KPIs the chosen locations cover, before tag or search filters — this is
   * what the Module filter derives its options from, so it only ever offers
   * modules that actually exist in the selected locations.
   */
  const locationScopedKpis = useMemo(
    () =>
      kpis.filter((kpi) =>
        passesFilters(kpi, assignments, {
          locationIds,
          filterSteps: [],
          search: "",
        }),
      ),
    [kpis, assignments, locationIds],
  );

  /** KPI rows keyed by id, so a pruned selection can re-check membership. */
  const kpiById = useMemo(
    () => new Map(kpis.map((kpi) => [kpi.kpiId, kpi])),
    [kpis],
  );

  /**
   * Splits the on-screen selection into KPIs a bulk date can fill (`eligible`,
   * no deadline yet) versus those that already have one (`withDate`). Both are
   * id arrays: the split drives the inline note, gates the overwrite prompt, and
   * supplies the exact ids each choice in that prompt stages the date onto.
   */
  const selectedSplit = useMemo(() => {
    const visibleIds = new Set(visibleKpis.map((kpi) => kpi.kpiId));
    const eligible = [];
    const withDate = [];
    for (const id of selected) {
      if (!visibleIds.has(id)) continue;
      if (kpiHasDate(kpiById.get(id))) withDate.push(id);
      else eligible.push(id);
    }
    return { eligible, withDate };
  }, [selected, visibleKpis, kpiById, kpiHasDate]);

  /** Location choices for the multi-select; an empty selection means all. */
  const locationOptions = useMemo(
    () =>
      locations.map((loc) => ({
        value: Number(loc.id),
        label: locationLabel(loc),
      })),
    [locations],
  );

  /** The chosen options, derived from ids so the control stays controlled. */
  const selectedLocationOptions = useMemo(
    () => locationOptions.filter((opt) => locationIds.includes(opt.value)),
    [locationOptions, locationIds],
  );

  const toggle = useCallback((kpiId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(kpiId)) next.delete(kpiId);
      else next.add(kpiId);
      return next;
    });
  }, []);

  /**
   * Ticks every KPI currently on screen, or unticks them all when they are
   * already selected. Scoped to the visible rows so it always agrees with the
   * filters — never staging a date onto a KPI the user cannot see.
   */
  const toggleAllVisible = () => {
    const visibleIds = visibleKpis.map((kpi) => kpi.kpiId);
    setSelected((prev) => {
      const next = new Set(prev);
      const allOn =
        visibleIds.length > 0 && visibleIds.every((id) => next.has(id));
      for (const id of visibleIds) {
        if (allOn) next.delete(id);
        else next.add(id);
      }
      return next;
    });
  };

  const stageDate = useCallback((kpiId, date) => {
    setPending((prev) => {
      const next = new Map(prev);
      if (date) next.set(kpiId, date);
      else next.delete(kpiId);
      return next;
    });
  }, []);

  /**
   * Stages the bulk date onto the given KPIs, then clears the selection and
   * reports what changed. The caller decides the set — the blank rows alone, or
   * those plus the ones being deliberately overwritten — so this stays the one
   * place that actually commits a bulk fill.
   * @param {number[]} kpiIds the KPIs to stage the bulk date onto.
   * @param {number} [overwritten] how many of them already had a due date.
   */
  const commitBulk = (kpiIds, overwritten = 0) => {
    if (!bulkDate || kpiIds.length === 0) return;
    setPending((prev) => {
      const next = new Map(prev);
      for (const kpiId of kpiIds) next.set(kpiId, bulkDate);
      return next;
    });
    setSelected(new Set());
    setBulkDate("");
    setConfirmOverwrite(false);
    setFeedback({
      ok: true,
      text: overwritten
        ? `Staged ${kpiIds.length} due date${
            kpiIds.length !== 1 ? "s" : ""
          } (${overwritten} overwritten).`
        : `Staged ${kpiIds.length} due date${kpiIds.length !== 1 ? "s" : ""}.`,
    });
  };

  /**
   * Applies the bulk date. Blank rows are filled straight away; if the selection
   * also includes KPIs that already have a deadline, the user is first asked
   * whether to overwrite those — a date in force is never changed without an
   * explicit yes.
   */
  const applyBulk = () => {
    if (!bulkDate) return;
    const { eligible, withDate } = selectedSplit;
    if (eligible.length === 0 && withDate.length === 0) return;
    if (withDate.length === 0) {
      commitBulk(eligible);
      return;
    }
    setConfirmOverwrite(true);
  };

  /**
   * Switches the financial year, discarding any staged edits — a staged date
   * belongs to the FY it was picked under and must not carry into another.
   * @param {string} id the chosen financial year id.
   */
  const changeFY = (id) => {
    setSelectedFY(fyData.find((fy) => String(fy.id) === id) ?? latestFY);
    setPending(new Map());
    setSelected(new Set());
    setBulkDate("");
    setFeedback(null);
  };

  /**
   * Drops from the selection any KPI the given filters would hide, so a bulk
   * date is never applied to a row the user can no longer see.
   * @param {number[]} nextLocationIds the location filter after the change.
   * @param {object[]} nextSteps the tag-filter steps after the change.
   */
  const pruneSelection = (nextLocationIds, nextSteps) => {
    setSelected((prev) => {
      const next = new Set();
      for (const id of prev) {
        const kpi = kpiById.get(id);
        if (
          kpi &&
          passesFilters(kpi, assignments, {
            locationIds: nextLocationIds,
            filterSteps: nextSteps,
            search,
          })
        ) {
          next.add(id);
        }
      }
      return next;
    });
  };

  /**
   * Changes the viewing locations, then drops any module/tag filter no longer
   * available in the new locations and prunes any now-hidden selection.
   * @param {{value: number}[]} options the chosen react-select options.
   */
  const changeLocations = (options) => {
    const ids = (options ?? []).map((opt) => opt.value);
    const scoped = kpis.filter((kpi) =>
      passesFilters(kpi, assignments, {
        locationIds: ids,
        filterSteps: [],
        search: "",
      }),
    );
    const nextSteps = pruneStepsToScope(filterSteps, scoped);
    setLocationIds(ids);
    setFilterSteps(nextSteps);
    pruneSelection(ids, nextSteps);
  };

  /** Changes the tag filter and prunes any now-hidden selection. */
  const changeSteps = (steps) => {
    setFilterSteps(steps);
    pruneSelection(locationIds, steps);
  };

  /** Persists every staged KPI date in one request. */
  const saveAll = async () => {
    setSaving(true);
    const res = await setDueDatesBulk(
      [...pending.entries()].map(([questionId, dueDate]) => ({
        financialYearId: selectedFY.id,
        questionnaireType: QUANT,
        scopeType: KPI_SCOPE,
        questionId: Number(questionId),
        dueDate,
      })),
    );

    if (!res.isSuccess) {
      setSaving(false);
      setFeedback({ ok: false, text: res.message || "Could not save" });
      return;
    }
    const skipped = res.data?.skipped ?? [];
    setPending(new Map());
    // Rebuild the Query Builder dataset so the new deadlines show there (and in
    // reminders) right away, rather than on the next hourly sync. Best-effort:
    // the dates are already saved, so a sync hiccup shouldn't read as a failure.
    await resyncDatasets();
    setSaving(false);
    refresh();
    setFeedback({
      ok: skipped.length === 0,
      text: skipped.length
        ? `Saved ${res.data.saved.length}, skipped ${skipped.length}.`
        : "Due dates saved.",
    });
  };

  /**
   * Removes a KPI's own saved due date, reverting it to any inherited deadline.
   * @param {number} id the KPI-scope due date to clear.
   */
  const removeOwnDate = async (id) => {
    if (
      !window.confirm(
        "Remove this KPI's own due date? It reverts to any location or module deadline, or none.",
      )
    ) {
      return;
    }
    const res = await clearDueDate(id);
    if (!res.isSuccess) {
      setFeedback({ ok: false, text: res.message || "Could not remove the due date" });
      return;
    }
    setEditingKpi(null);
    await resyncDatasets();
    refresh();
    setFeedback({ ok: true, text: "Due date removed." });
  };

  const selectedVisibleCount = visibleKpis.reduce(
    (count, kpi) => count + (selected.has(kpi.kpiId) ? 1 : 0),
    0,
  );
  const allVisibleSelected =
    visibleKpis.length > 0 && selectedVisibleCount === visibleKpis.length;
  const someVisibleSelected =
    selectedVisibleCount > 0 && !allVisibleSelected;

  return (
    <div className="ddt-root">
      <p className="ddt-muted">
        Set when each KPI's data is due. Once a due date passes, entry for that
        KPI is locked until an admin grants an extension.
      </p>

      <div className="ddt-toolbar">
        <select
          className="ddt-select"
          value={selectedFY?.id ?? ""}
          aria-label="Financial year"
          onChange={(e) => changeFY(e.target.value)}
        >
          {fyData.map((fy) => (
            <option key={fy.id} value={fy.id}>
              {fy.financial_year_value ?? fy.year ?? fy.id}
            </option>
          ))}
        </select>

        <Select
          isMulti
          className="ddt-locselect"
          classNamePrefix="ddt-locsel"
          styles={LOCATION_SELECT_STYLES}
          options={locationOptions}
          value={selectedLocationOptions}
          onChange={changeLocations}
          placeholder="All locations"
          aria-label="Viewing locations"
          closeMenuOnSelect={false}
          noOptionsMessage={() => "No locations"}
        />

        <input
          className="ddt-input ddt-grow"
          value={search}
          placeholder="Search KPIs by title…"
          aria-label="Search KPIs"
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {isViewOnly && (
        <p className="ddt-warn">
          {selectedFY?.id !== latestFY?.id
            ? "You are viewing a past financial year. Due dates can only be changed for the current one."
            : "You have view-only access to due dates. Ask an administrator to change them."}
        </p>
      )}

      <TagFilterBar
        allQuestions={locationScopedKpis}
        filterSteps={filterSteps}
        onFilterStepsChange={changeSteps}
      />

      {selected.size > 0 && !isViewOnly && (
        <div className="ddt-bulk-wrap">
          <div className="ddt-bulk">
            <span className="ddt-bulk-count">{selected.size} selected</span>
            <input
              type="date"
              className="ddt-date"
              value={bulkDate}
              min={todayISO()}
              aria-label="Due date to apply in bulk"
              onChange={(e) => setBulkDate(e.target.value)}
            />
            <button
              type="button"
              className="ddt-btn"
              disabled={
                !bulkDate ||
                (selectedSplit.eligible.length === 0 &&
                  selectedSplit.withDate.length === 0)
              }
              onClick={applyBulk}
            >
              Bulk apply
            </button>
            <button
              type="button"
              className="ddt-btn ddt-btn-ghost"
              onClick={() => setSelected(new Set())}
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="ddt-error" role="alert">
          {error}
        </div>
      )}

      <div className="ddt-list">
        <div className="ddt-head">
          {!isViewOnly && visibleKpis.length > 0 ? (
            <input
              type="checkbox"
              className="ddt-check"
              checked={allVisibleSelected}
              ref={(el) => {
                if (el) el.indeterminate = someVisibleSelected;
              }}
              onChange={toggleAllVisible}
              aria-label="Select all KPIs"
            />
          ) : (
            <span />
          )}
          <span>KPI</span>
          <span>Due date</span>
          <span />
          <span />
        </div>

        {loading ? (
          <p className="ddt-empty">Loading…</p>
        ) : visibleKpis.length === 0 ? (
          <p className="ddt-empty">
            {locationIds.length
              ? "No KPIs are assigned to the selected location(s)."
              : "No KPIs match the current filters."}
          </p>
        ) : (
          visibleKpis.map((kpi) => {
            const resolved = resolveRowDueDate(
              kpi,
              applicableLocations(kpi),
              index,
            );
            return (
              <DueDateRow
                key={kpi.kpiId}
                kpi={kpi}
                selected={selected.has(kpi.kpiId)}
                dueDate={resolved?.dueDate ?? null}
                source={resolved?.source ?? null}
                pending={pending.get(kpi.kpiId) ?? null}
                onToggle={toggle}
                onConfigure={setEditingKpi}
                disabled={isViewOnly}
              />
            );
          })
        )}
      </div>

      {editingKpi && (
        <DueDateEditorModal
          kpi={editingKpi}
          dueDate={editingResolved?.dueDate ?? null}
          source={editingResolved?.source ?? null}
          pending={pending.get(editingKpi.kpiId) ?? null}
          ownId={editingOwnId}
          onStage={stageDate}
          onRemove={removeOwnDate}
          onClose={() => setEditingKpi(null)}
        />
      )}

      {confirmOverwrite && (
        <BulkOverwriteModal
          date={bulkDate}
          eligibleCount={selectedSplit.eligible.length}
          withDateCount={selectedSplit.withDate.length}
          onOverwriteAll={() =>
            commitBulk(
              [...selectedSplit.eligible, ...selectedSplit.withDate],
              selectedSplit.withDate.length,
            )
          }
          onFillBlanks={() => commitBulk(selectedSplit.eligible)}
          onCancel={() => setConfirmOverwrite(false)}
        />
      )}

      {pending.size > 0 && !isViewOnly && (
        <div className="ddt-footer">
          <span>
            {pending.size} unsaved change{pending.size !== 1 ? "s" : ""}
          </span>
          <button
            type="button"
            className="ddt-btn"
            disabled={saving}
            onClick={saveAll}
          >
            {saving ? "Saving…" : "Save all"}
          </button>
        </div>
      )}

      {feedback && (
        <p className={feedback.ok ? "ddt-muted" : "ddt-error"} role="status">
          {feedback.text}
        </p>
      )}
    </div>
  );
}
